import React, { createContext, useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { login as apiLogin, verifyToken as apiVerifyToken, logout as apiLogout } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [token, setToken] = useState(localStorage.getItem('token') || null);




  // Check if user is logged in on initial load
  useEffect(() => {
    console.log('AuthProvider: Checking for existing session...');
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          console.log('No token found, user is not authenticated');
          setLoading(false);
          return;
        }

        console.log('Verifying token with server...');
        const response = await apiVerifyToken();
        if (response.valid) {
          console.log('User session verified:', response.user);
          setUser(response.user);
        } else {
          console.log('Invalid or expired session');
          localStorage.removeItem('token');
        }
      } catch (error) {
        console.error('Auth check error:', error);
        if (error.response) {
          console.error('Error response:', error.response.data);
        }
        localStorage.removeItem('token');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (email, password) => {
    console.log('Attempting login with email:', email);
    setError(null);
    try {
      console.log('Sending login request to /api/auth/login');
      const response = await apiLogin(email, password);
      console.log('Login response:', response);
      
      if (response.token) {
        console.log('Login successful, storing token and user data');
        localStorage.setItem('token', response.token);
        setUser(response.user);
        setToken(response.token);
        
        // Get the intended destination or default to '/dashboard'
        const from = window.history.state?.from?.pathname || '/dashboard';
        console.log('Navigating to:', from);
        
        // Force a page reload to ensure all auth state is properly initialized
        window.location.href = from;
        
        return { success: true };
      } else {
        console.log('No token in response:', response);
        throw new Error('No token received from server');
      }
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.message || 'Login failed. Please try again.';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    }
  };

  const logout = async () => {
    console.log('Logging out user');
    try {
      await apiLogout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      setUser(null);
      setToken(null);
      navigate('/login');
    }
  };

  const refreshPermissions = async () => {
    console.log('Refreshing user permissions...');
    try {
      const response = await fetch('/api/auth/refresh-permissions', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to refresh permissions');
      }

      const data = await response.json();
      console.log('Permissions refreshed:', data);
      
      // Update the token and user data
      localStorage.setItem('token', data.token);
      setToken(data.token);
      setUser(data.user);
      
      return { success: true, user: data.user };
    } catch (error) {
      console.error('Error refreshing permissions:', error);
      return { success: false, error: error.message };
    }
  };

  // API wrapper with auth - now using the centralized API service
  const apiRequest = async (method, url, data = null) => {
    const requestUrl = url.startsWith('/api/') ? url : `/api${url}`;
    
    try {
      // Import the API methods dynamically to avoid circular imports
      const { get, post, put, del } = await import('../services/api');
      
      let response;
      switch (method.toLowerCase()) {
        case 'get':
          response = await get(requestUrl);
          break;
        case 'post':
          response = await post(requestUrl, data);
          break;
        case 'put':
          response = await put(requestUrl, data);
          break;
        case 'delete':
          response = await del(requestUrl);
          break;
        default:
          throw new Error(`Unsupported method: ${method}`);
      }
      
      return { 
        data: response, 
        error: null,
        status: 200 
      };
    } catch (error) {
      console.error('API Request Error:', {
        message: error.message,
        url: requestUrl,
        method,
      });
      
      return { 
        data: null, 
        error: error.message || 'Network error',
        status: 0
      };
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    refreshPermissions,
    api: apiRequest,
    isAuthenticated: !!user,
    authToken: token,
    setUser,
    setToken,
  };

  return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
